import type { ColumnDef } from '@tanstack/react-table';
import type { TestRunListItem } from '~/server/api/routers/test';
import { prettyDuration } from '~/common/time/duration';
import ResultSummary from '~/components/test/run/ResultSummary';
import TimeAgo from '~/components/time/TimeAgo';
import { ActionIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconVideo } from '@tabler/icons-react';
import { getTraceUrl } from '~/common/trace/getTraceUrl';
import Link from 'next/link';
import { GitBranchBadge } from '~/components/common/GitBranchBadge';
import LayeredRunAttemptStatusIcons from '~/components/icons/LayeredRunAttemptStatusIcons';
import { VersionBadge } from '~/components/common/VersionBadge';
import { Skeleton } from '@mantine/core';
import type { api } from '~/trpc-extra/api';

export const createRunColumns = (utils: ReturnType<typeof api.useUtils>): ColumnDef<TestRunListItem>[] => [
  {
    id: 'id',
    accessorFn: (row) => row.id,
    enableColumnFilter: false,
    header: () => <span>Run ID</span>,
    cell: ({ row }) => {
      return (
        <Link
          className="hover:underline"
          href={`/project/${row.original.testV2.projectId}/history/s_${row.original.id}/run/${row.original.id}?from=tests`}
        >
          #{row.original.id.slice(0, 8)}
        </Link>
      );
    },
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 50 + 50}px`}
        />
      ),
    },
  },

  {
    id: 'result',
    header: 'Result',
    enableColumnFilter: false,
    cell: ({ row }) => (
      <div className="flex flex-row gap-2">
        <LayeredRunAttemptStatusIcons
          statusHistory={row.original.statusHistory ?? undefined}
          attempts={row.original.TestRunAttempts}
          runStatus={row.original.status}
        />
        <ResultSummary
          executionError={row.original.TestRunAttempts.at(-1)?.AttemptError ?? undefined}
          failedAssertions={row.original.TestRunAttempts.at(-1)?.failedAssertions || []}
          status={row.original.status}
        />
      </div>
    ),
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 80 + 80}px`}
        />
      ),
    },
  },
  {
    id: 'updatedAt',
    accessorFn: (row) => row.updatedAt,
    header: 'Updated',
    enableColumnFilter: false,
    cell: ({ row }) => <TimeAgo time={row.original.updatedAt} />,
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 100 + 100}px`}
        />
      ),
    },
  },
  {
    id: 'inputContentId',
    accessorFn: (row) => row.TestRunAttempts.at(-1)?.inputTestContentId,
    header: 'Version',
    enableColumnFilter: false,
    cell: ({ row }) => {
      return (
        <VersionBadge
          contentId={row.original.TestRunAttempts.at(-1)?.inputTestContentId || ''}
          trunkContentId={row.original.testV2.trunkContentId || ''}
          testId={row.original.testV2Id}
        />
      );
    },
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 100 + 100}px`}
        />
      ),
    },
  },
  {
    id: 'branch',
    header: 'Branch',
    enableColumnFilter: false,
    cell: ({ row }) =>
      row.original.testSuiteRun?.gitBranch ? <GitBranchBadge gitBranch={row.original.testSuiteRun?.gitBranch} /> : null,
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 75 + 75}px`}
        />
      ),
    },
  },
  {
    id: 'duration',
    header: 'Duration',
    enableColumnFilter: false,
    cell: ({ row }) =>
      row.original.status === 'RUNNING' ?
        '-'
      : prettyDuration(
          Math.ceil(
            (row.original.durationMs ?? row.original.updatedAt.getTime() - row.original.createdAt.getTime()) / 1000,
          ),
        ),
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 50 + 50}px`}
        />
      ),
    },
  },
  {
    id: 'replay',
    enableColumnFilter: false,
    cell: ({ row }) => {
      const clickable = row.original.status !== 'RUNNING' && row.original.status !== 'SKIPPED';

      return (
        <ActionIcon
          variant="transparent"
          title="Open Trace"
          disabled={!clickable}
          onClick={async (e) => {
            e.stopPropagation();
            const traceIdentifier = {
              projectId: row.original.testV2.projectId,
              testId: row.original.testV2Id,
              testRunId: row.original.id,
              attemptId: row.original.TestRunAttempts[0]?.id || '',
            };
            const { presignedUrl } = await utils.trace.getPresignedUrl.fetch({ traceIdentifier });
            if (!presignedUrl) {
              notifications.show({
                color: 'yellow',
                title: 'No trace available',
                message:
                  'No trace was found for this run. If it just finished, it may still be uploading — try again shortly.',
              });
              return;
            }
            open(getTraceUrl({ traceZipUrl: presignedUrl }), '_blank');
          }}
        >
          <IconVideo />
        </ActionIcon>
      );
    },
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 50 + 50}px`}
        />
      ),
    },
  },
];
